import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Backspace, ArrowRight } from "@phosphor-icons/react";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";

export default function AdminLogin() {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role) navigate(homeForRole(user.role), { replace: true });
  }, [user, navigate]);

  const press = (n) => {
    if (loading) return;
    setError("");
    setPin((p) => (p.length >= 6 ? p : p + n));
  };
  const back = () => setPin((p) => p.slice(0, -1));
  const clear = () => setPin("");

  const submit = async (toSubmit = pin) => {
    if (toSubmit.length < 4) {
      setError("PIN debe tener al menos 4 dígitos");
      return;
    }
    setLoading(true);
    try {
      const u = await login(toSubmit);
      navigate(homeForRole(u.role), { replace: true });
    } catch (e) {
      setError(formatErr(e));
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-pos)] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-orange-300/20 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-brand text-white items-center justify-center font-display font-black text-2xl shadow-lg mb-5">JR</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Acceso del Personal</h1>
          <p className="text-zinc-500 text-sm mt-2">Ingresa tu PIN numérico para continuar</p>
        </div>

        <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm p-8">
          <div className="flex justify-center gap-3 mb-8" data-testid="pin-display">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? "bg-brand scale-110" : "bg-zinc-200"}`} />
            ))}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-5 text-sm font-medium text-center" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            {[1,2,3,4,5,6,7,8,9].map((n) => (
              <button key={n} onClick={() => press(String(n))} className="numpad-btn" data-testid={`pos-login-numpad-${n}`}>{n}</button>
            ))}
            <button onClick={clear} className="numpad-btn text-sm text-zinc-500" data-testid="pos-login-numpad-clear">C</button>
            <button onClick={() => press("0")} className="numpad-btn" data-testid="pos-login-numpad-0">0</button>
            <button onClick={back} className="numpad-btn flex items-center justify-center" data-testid="pos-login-numpad-back"><Backspace size={28} weight="bold" /></button>
          </div>

          <button
            onClick={() => submit()}
            disabled={loading || pin.length < 4}
            className="btn-brand w-full h-14 mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="pos-login-submit"
          >
            {loading ? "Validando..." : (<>Entrar <ArrowRight size={20} weight="bold" /></>)}
          </button>

          <div className="text-center mt-6 text-xs text-zinc-400">
            <span>Tip: prueba con 1234 (admin), 1111 (mesero), 2222 (caja), 3333 (taquero)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
